import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import './Account.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const formatPrice = (price) =>
  price != null ? `₹${parseFloat(price).toFixed(2)}` : '—';

const r2 = (n) => Math.round(n * 100) / 100;

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

const statusLabel = (order) => {
  const s = order.order_lc_state || order.order_state || '—';
  return s;
};

const statusClass = (order) => {
  const s = (order.order_lc_state || order.order_state || '').toLowerCase();
  if (s === 'placed' || s === 'draft') return 'status-placed';
  if (s === 'delivered') return 'status-delivered';
  if (s === 'cancelled') return 'status-cancelled';
  return 'status-default';
};

// ── Pay Now modal ─────────────────────────────────────────
const PaymentModal = ({ order, user, onClose, onPaid }) => {
  const auth = { Authorization: `Bearer ${user.token}` };
  const [walletBalance, setWalletBalance] = useState(null);
  const [cashUsed, setCashUsed]     = useState(0);
  const [pointsUsed, setPointsUsed] = useState(0);
  const [phase, setPhase]           = useState('loading');
  const [error, setError]           = useState('');
  const [rzOrderId, setRzOrderId]   = useState(null);

  const orderAmount = parseFloat(order.order_amount);

  useEffect(() => {
    axios.get(`${API_URL}/wallet/balance`, { headers: auth })
      .then(res => {
        const bal = res.data;
        setWalletBalance(bal);
        const maxCash   = r2(Math.min(parseFloat(bal.cash_balance   || 0), orderAmount));
        const maxPoints = r2(Math.min(parseFloat(bal.points_balance || 0), Math.max(0, orderAmount - maxCash)));
        setCashUsed(maxCash);
        setPointsUsed(maxPoints);
        setPhase('ready');
      })
      .catch(() => { setWalletBalance(null); setPhase('ready'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasCash    = parseFloat(walletBalance?.cash_balance   || 0) > 0;
  const hasPoints  = parseFloat(walletBalance?.points_balance || 0) > 0;
  const hasWallet  = hasCash || hasPoints;
  const razorpayDue = r2(Math.max(0, orderAmount - cashUsed - pointsUsed));

  const cancelPending = (orderId) => {
    if (!orderId) return;
    axios.post(`${API_URL}/pay/pay-existing/cancel`, { razorpay_order_id: orderId }, { headers: auth })
      .catch(() => { /* fire-and-forget */ });
    setRzOrderId(null);
  };

  const handlePay = async () => {
    setError('');
    setPhase('processing');
    const payload = { order_id: order.id, wallet_cash_amount: cashUsed, wallet_points_amount: pointsUsed };

    // ── Pure wallet ──────────────────────────────────────────────────
    if (razorpayDue < 0.01 && (cashUsed > 0 || pointsUsed > 0)) {
      try {
        await axios.post(`${API_URL}/pay/pay-existing/create-order`, payload, { headers: auth });
        onPaid(order.id);
      } catch (err) {
        setError(err.response?.data?.error || 'Payment failed. Please try again.');
        setPhase('ready');
      }
      return;
    }

    // ── Razorpay (full or split) ─────────────────────────────────────
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setError('Failed to load payment SDK. Please check your connection.');
      setPhase('ready');
      return;
    }

    try {
      const { data } = await axios.post(`${API_URL}/pay/pay-existing/create-order`, payload, { headers: auth });

      setRzOrderId(data.razorpay_order_id);

      const rzp = new window.Razorpay({
        key:      data.key,
        amount:   data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name:        'Local Heros',
        description: `Payment for order ${order.order_no}`,
        prefill: { name: user.name, email: user.email, contact: user.phone },
        theme: { color: '#C9A227' },
        handler: async (response) => {
          try {
            await axios.post(`${API_URL}/pay/pay-existing/verify`, {
              order_id:            order.id,
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              wallet_cash_amount:   cashUsed,
              wallet_points_amount: pointsUsed,
            }, { headers: auth });
            onPaid(order.id);
          } catch (err) {
            setError(err.response?.data?.error || 'Payment received but order update failed. Please contact support.');
            setPhase('ready');
          }
        },
        modal: {
          ondismiss: () => {
            cancelPending(data.razorpay_order_id);
            setPhase('ready');
          },
        },
      });
      rzp.on('payment.failed', (response) => {
        cancelPending(data.razorpay_order_id);
        setError(`Payment failed: ${response.error.description}`);
        setPhase('ready');
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
      setPhase('ready');
    }
  };

  const btnLabel = razorpayDue < 0.01
    ? 'Pay with Wallet'
    : (cashUsed > 0 || pointsUsed > 0)
      ? `Pay ₹${razorpayDue.toFixed(2)} via Razorpay`
      : `Pay ₹${orderAmount.toFixed(2)}`;

  return (
    <div className="acct-pay-overlay" onClick={() => phase !== 'processing' && onClose()}>
      <div className="acct-pay-modal" onClick={e => e.stopPropagation()}>
        <div className="acct-pay-header">
          <h3>Pay for Order {order.order_no}</h3>
          <button className="acct-pay-close" onClick={onClose} disabled={phase === 'processing'}>×</button>
        </div>

        <div className="acct-pay-body">
          <div className="acct-pay-amount-row">
            <span>Order Amount</span>
            <strong>{formatPrice(order.order_amount)}</strong>
          </div>

          {phase === 'loading' && <p className="acct-loading" style={{ margin: '12px 0 0' }}>Loading wallet…</p>}

          {phase !== 'loading' && hasWallet && (
            <div className="acct-pay-wallet-section">
              <p className="acct-pay-wallet-title">Use Wallet Balance</p>
              {hasCash && (
                <div className="acct-pay-wallet-row">
                  <span>Cash <span className="acct-pay-avail">(Available: ₹{parseFloat(walletBalance.cash_balance).toFixed(2)})</span></span>
                  <input
                    type="number"
                    className="acct-pay-wallet-input"
                    min="0"
                    max={Math.min(parseFloat(walletBalance.cash_balance), orderAmount)}
                    step="0.01"
                    value={cashUsed}
                    onChange={e => {
                      const v = parseFloat(e.target.value) || 0;
                      const maxCash = Math.min(parseFloat(walletBalance.cash_balance), orderAmount);
                      const clamped = r2(Math.max(0, Math.min(v, maxCash)));
                      setCashUsed(clamped);
                      const maxPts = Math.min(parseFloat(walletBalance.points_balance), Math.max(0, orderAmount - clamped));
                      setPointsUsed(r2(Math.min(pointsUsed, maxPts)));
                    }}
                  />
                </div>
              )}
              {hasPoints && (
                <div className="acct-pay-wallet-row">
                  <span>Points <span className="acct-pay-avail">(Available: {parseFloat(walletBalance.points_balance).toFixed(0)} pts)</span></span>
                  <input
                    type="number"
                    className="acct-pay-wallet-input"
                    min="0"
                    max={Math.min(parseFloat(walletBalance.points_balance), Math.max(0, orderAmount - cashUsed))}
                    step="0.01"
                    value={pointsUsed}
                    onChange={e => {
                      const v = parseFloat(e.target.value) || 0;
                      const remaining = Math.max(0, orderAmount - cashUsed);
                      const maxPts = Math.min(parseFloat(walletBalance.points_balance), remaining);
                      setPointsUsed(r2(Math.max(0, Math.min(v, maxPts))));
                    }}
                  />
                </div>
              )}
              {razorpayDue > 0.01 && (
                <div className="acct-pay-due-row">
                  <span>To pay via Razorpay</span>
                  <strong>₹{razorpayDue.toFixed(2)}</strong>
                </div>
              )}
            </div>
          )}

          {error && <p className="acct-pay-error">{error}</p>}
        </div>

        <div className="acct-pay-footer">
          <button className="acct-pay-cancel-btn" onClick={onClose} disabled={phase === 'processing'}>
            Close
          </button>
          <button
            className="acct-pay-btn"
            onClick={handlePay}
            disabled={phase === 'loading' || phase === 'processing'}
          >
            {phase === 'processing' ? 'Processing…' : btnLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Orders tab ────────────────────────────────────────────
const PAGE_SIZE = 5;

const OrdersTab = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [page, setPage] = useState(0);
  const [payingOrder, setPayingOrder] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/menuRoutes/my-orders`, {
        headers: { Authorization: `Bearer ${user.token}` },
      })
      .then((res) => { setOrders(res.data); setLoading(false); })
      .catch((err) => {
        console.error('Error fetching orders:', err);
        setError('Could not load orders.');
        setLoading(false);
      });
  }, [user.token]);

  if (loading) return <p className="acct-loading">Loading orders…</p>;
  if (error)   return <p className="acct-error">{error}</p>;
  if (orders.length === 0) return (
    <div className="acct-empty">
      <p>You haven't placed any orders yet.</p>
    </div>
  );

  const totalPages = Math.ceil(orders.length / PAGE_SIZE);
  const pageOrders = orders.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="acct-orders">
      {pageOrders.map((order) => (
        <div key={order.id} className="acct-order-card">
          <button
            className="acct-order-header"
            onClick={() => setExpanded(expanded === order.id ? null : order.id)}
          >
            <div className="acct-order-meta">
              <span className="acct-order-no">{order.order_no}</span>
              <span className="acct-order-date">{formatDate(order.order_date)}</span>
            </div>
            <div className="acct-order-right">
              {order.order_lc_state === 'Payment Requested' ? (
                <button
                  className="acct-pay-now-btn"
                  onClick={e => { e.stopPropagation(); setPayingOrder(order); }}
                >
                  Pay Now
                </button>
              ) : (
                <span className={`acct-order-status ${statusClass(order)}`}>
                  {statusLabel(order)}
                </span>
              )}
              <span className="acct-order-amount">{formatPrice(order.order_amount)}</span>
              <span className="acct-order-chevron">
                {expanded === order.id ? '▲' : '▼'}
              </span>
            </div>
          </button>

          {expanded === order.id && (
            <div className="acct-order-body">
              {/* Delivery info */}
              {(order.delivery_date || order.delivery_window) && (
                <div className="acct-delivery-info">
                  {order.delivery_date && (
                    <span>&#128337; {formatDate(order.delivery_date)}</span>
                  )}
                  {order.delivery_window && (
                    <span>&#128336; {order.delivery_window}</span>
                  )}
                </div>
              )}

              {/* Line items */}
              {order.lines.length > 0 ? (
                <table className="acct-lines-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th className="acct-col-num">Qty</th>
                      <th className="acct-col-num">Unit Price</th>
                      <th className="acct-col-num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.lines.map((line, i) => (
                      <tr key={i}>
                        <td>{line.product_name}</td>
                        <td className="acct-col-num">{line.qty}</td>
                        <td className="acct-col-num">{formatPrice(line.unit_price)}</td>
                        <td className="acct-col-num">{formatPrice(line.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} className="acct-total-label">Order Total</td>
                      <td className="acct-col-num acct-total-value">
                        {formatPrice(order.order_amount)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p className="acct-no-lines">No items on this order.</p>
              )}
            </div>
          )}
        </div>
      ))}
      {totalPages > 1 && (
        <div className="acct-pagination">
          <button
            className="acct-page-btn"
            onClick={() => { setPage(p => p - 1); setExpanded(null); }}
            disabled={page === 0}
          >
            &#8592;
          </button>
          <span className="acct-page-info">{page + 1} / {totalPages}</span>
          <button
            className="acct-page-btn"
            onClick={() => { setPage(p => p + 1); setExpanded(null); }}
            disabled={page === totalPages - 1}
          >
            &#8594;
          </button>
        </div>
      )}

      {payingOrder && (
        <PaymentModal
          order={payingOrder}
          user={user}
          onClose={() => setPayingOrder(null)}
          onPaid={(orderId) => {
            setOrders(prev => prev.map(o =>
              o.id === orderId ? { ...o, order_lc_state: 'Placed' } : o
            ));
            setPayingOrder(null);
          }}
        />
      )}
    </div>
  );
};

// ── Change Password modal ──────────────────────────────────
const ChangePasswordModal = ({ user, onClose }) => {
  const { refreshUser } = useContext(AuthContext);
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [phase, setPhase] = useState('idle'); // idle | loading | success
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!form.current) { setError('Enter your current password.'); return; }
    if (form.next.length < 4) { setError('New password must be at least 4 characters.'); return; }
    if (form.next !== form.confirm) { setError('New passwords do not match.'); return; }

    setPhase('loading');
    try {
      await axios.post(
        `${API_URL}/authRoutes/change-password`,
        { current_password: form.current, new_password: form.next },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      await refreshUser();
      setPhase('success');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not change password. Please try again.');
      setPhase('idle');
    }
  };

  return (
    <div className="acct-pay-overlay" onClick={() => phase !== 'loading' && onClose()}>
      <div className="acct-pay-modal" onClick={e => e.stopPropagation()}>
        <div className="acct-pay-header">
          <h3>Change Password</h3>
          <button className="acct-pay-close" onClick={onClose} disabled={phase === 'loading'}>&#215;</button>
        </div>

        <div className="acct-pay-body">
          {phase === 'success' ? (
            <p style={{ textAlign: 'center', color: '#27ae60', fontWeight: 600, padding: '8px 0' }}>
              Password changed successfully.
            </p>
          ) : (
            <>
              {[
                { key: 'current', label: 'Current Password' },
                { key: 'next',    label: 'New Password' },
                { key: 'confirm', label: 'Confirm New Password' },
              ].map(({ key, label }) => (
                <div key={key} className="acct-chpw-field">
                  <label className="acct-chpw-label">{label}</label>
                  <input
                    type="password"
                    className="acct-chpw-input"
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                    autoComplete="new-password"
                  />
                </div>
              ))}
              {error && <p className="acct-pay-error">{error}</p>}
            </>
          )}
        </div>

        <div className="acct-pay-footer">
          <button className="acct-pay-cancel-btn" onClick={onClose} disabled={phase === 'loading'}>
            {phase === 'success' ? 'Close' : 'Cancel'}
          </button>
          {phase !== 'success' && (
            <button
              className="acct-pay-btn"
              onClick={handleSubmit}
              disabled={phase === 'loading'}
            >
              {phase === 'loading' ? 'Changing…' : 'Change Password'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Profile Details Modal ─────────────────────────────────
const fmtMMDD = (s) => {
  if (!s || s === '00-00') return '—';
  const [mm, dd] = s.split('-');
  if (!mm || !dd || mm === '00' || dd === '00') return '—';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(dd, 10)} ${months[parseInt(mm, 10) - 1] || ''}`;
};

const fmtOccDate = (s) => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const ProfileDetailsModal = ({ user, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/user-profile/me`, {
      headers: { Authorization: `Bearer ${user.token}` },
    }).then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user.token]); // eslint-disable-line

  const handleEdit = () => {
    onClose();
    navigate('/know-you-better?edit=true');
  };

  const profile = data?.profile;
  const family = data?.family || [];
  const occasions = data?.occasions || [];

  const MEMBER_LABEL = { SPOUSE: '💑 Spouse', PARENT: '👨‍👩‍ Parent', KID: '👶 Child' };

  const Row = ({ label, value }) => value && value !== '—' ? (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #F5F0E8', fontSize:'14px' }}>
      <span style={{ color:'#8B6914', fontWeight:600, minWidth:'140px' }}>{label}</span>
      <span style={{ color:'#333', textAlign:'right', flex:1 }}>{value}</span>
    </div>
  ) : null;

  return (
    <div className="acct-pay-overlay" onClick={onClose}>
      <div className="acct-pay-modal" style={{ maxWidth:'520px', maxHeight:'85vh', display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>
        <div className="acct-pay-header">
          <h3>My Profile Details</h3>
          <button className="acct-pay-close" onClick={onClose}>&#215;</button>
        </div>

        <div className="acct-pay-body" style={{ overflowY:'auto', flex:1 }}>
          {loading ? (
            <p style={{ textAlign:'center', color:'#9B7222', padding:'20px 0' }}>Loading…</p>
          ) : !profile && occasions.length === 0 && family.length === 0 ? (
            <p style={{ textAlign:'center', color:'#aaa', padding:'20px 0' }}>No profile details saved yet.</p>
          ) : (
            <>
              {/* About You */}
              {profile && (
                <div style={{ marginBottom:'18px' }}>
                  <div style={{ fontWeight:700, color:'#5C3D1E', fontSize:'13px', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px' }}>About You</div>
                  <Row label="Date of Birth"       value={fmtMMDD(profile.dob)} />
                  <Row label="Wedding Anniversary" value={fmtMMDD(profile.wedding_anniversary)} />
                  <Row label="Family Size"         value={profile.family_size} />
                  <Row label="Favourite Food"      value={profile.favourite_food} />
                  <Row label="Favourite Meat"      value={profile.favourite_meat} />
                  <Row label="Allergies"           value={profile.allergies} />
                  {profile.food_preferences?.length > 0 && (
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #F5F0E8', fontSize:'14px' }}>
                      <span style={{ color:'#8B6914', fontWeight:600, minWidth:'140px' }}>Food Preferences</span>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:'5px', justifyContent:'flex-end', flex:1 }}>
                        {profile.food_preferences.map(p => (
                          <span key={p} style={{ background:'#FFF3CD', color:'#7A5C2E', borderRadius:'12px', padding:'2px 10px', fontSize:'12px', fontWeight:600 }}>{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Special Occasions */}
              {occasions.length > 0 && (
                <div style={{ marginBottom:'18px' }}>
                  <div style={{ fontWeight:700, color:'#5C3D1E', fontSize:'13px', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px' }}>Special Occasions</div>
                  {occasions.map((o, i) => (
                    <Row key={i} label={o.occasion_name} value={fmtOccDate(o.occasion_date)} />
                  ))}
                </div>
              )}

              {/* Family */}
              {family.length > 0 && (
                <div>
                  <div style={{ fontWeight:700, color:'#5C3D1E', fontSize:'13px', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px' }}>Family</div>
                  {family.map((m, i) => (
                    <div key={i} style={{ background:'#FFFDF5', border:'1px solid #EDD98A', borderRadius:'10px', padding:'12px 14px', marginBottom:'10px' }}>
                      <div style={{ fontWeight:700, color:'#7A5C2E', fontSize:'13px', marginBottom:'8px' }}>
                        {MEMBER_LABEL[m.member_type] || m.member_type}
                        {m.name ? ` — ${m.name}` : ''}
                      </div>
                      <Row label="Date of Birth"       value={fmtMMDD(m.dob)} />
                      <Row label="Wedding Anniversary" value={fmtMMDD(m.wedding_anniversary)} />
                      {m.occasions?.map((o, j) => (
                        <Row key={j} label={o.occasion_name} value={fmtOccDate(o.occasion_date)} />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="acct-pay-footer">
          <button className="acct-pay-cancel-btn" onClick={onClose}>Close</button>
          <button className="acct-pay-btn" onClick={handleEdit}>Edit</button>
        </div>
      </div>
    </div>
  );
};

// ── Personal Details tab ──────────────────────────────────
const PersonalDetailsTab = ({ user }) => {
  const [address, setAddress] = useState(null);
  const [addrLoading, setAddrLoading] = useState(false);
  const [showChPw, setShowChPw] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  useEffect(() => {
    if (!user?.customer_id) return;
    setAddrLoading(true);
    axios
      .get(`${API_URL}/menuRoutes/customer-address`, {
        params: { customer_id: user.customer_id },
        headers: { Authorization: `Bearer ${user.token}` },
      })
      .then((res) => { setAddress(res.data.address || null); setAddrLoading(false); })
      .catch(() => setAddrLoading(false));
  }, [user]);

  const fmtDate = (s) => {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const rows = [
    { label: 'Full Name',     value: user?.name },
    { label: 'Email',         value: user?.email || user?.username },
    { label: 'Phone',         value: user?.phone },
    {
      label: 'Delivery Address',
      value: addrLoading
        ? 'Loading…'
        : (address || (user?.customer_id ? 'No address on file.' : 'No customer account linked.')),
    },
    { label: 'Member Since',  value: fmtDate(user?.created_date) },
    ...(user?.updated_date ? [{ label: 'Last Updated', value: fmtDate(user.updated_date) }] : []),
  ];

  return (
    <>
      <div style={{ position: 'relative' }}>
        <button className="acct-myprofile-btn" onClick={() => setShowProfile(true)}>
          My Profile
        </button>
        <div className="acct-details">
          {rows.map(({ label, value }) => (
            <div key={label} className="acct-detail-row">
              <span className="acct-detail-label">{label}</span>
              <span className="acct-detail-value">{value || '—'}</span>
            </div>
          ))}
          <div className="acct-detail-row acct-chpw-row">
            <span className="acct-detail-label">Password</span>
            <button className="acct-chpw-btn" onClick={() => setShowChPw(true)}>
              Change Password
            </button>
          </div>
        </div>
      </div>

      {showChPw && (
        <ChangePasswordModal user={user} onClose={() => setShowChPw(false)} />
      )}
      {showProfile && (
        <ProfileDetailsModal user={user} onClose={() => setShowProfile(false)} />
      )}
    </>
  );
};

// ── Referrals > Customers sub-tab ────────────────────────
const ReferredCustomersTab = ({ user }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/menuRoutes/referred-customers`, {
        headers: { Authorization: `Bearer ${user.token}` },
      })
      .then(res => { setCustomers(res.data); setLoading(false); })
      .catch(() => { setError('Could not load referred customers.'); setLoading(false); });
  }, [user.token]);

  if (loading) return <p className="acct-loading">Loading customers…</p>;
  if (error)   return <p className="acct-error">{error}</p>;
  if (customers.length === 0) return (
    <div className="acct-empty"><p>No referred customers yet.</p></div>
  );

  return (
    <div className="acct-ref-customers">
      {customers.map(c => (
        <div key={c.id} className="acct-ref-customer-card">
          <div className="acct-ref-customer-name">{c.name || '—'}</div>
          <div className="acct-ref-customer-meta">
            {c.phone && <span>&#128222; {c.phone}</span>}
            {c.email && <span>&#9993; {c.email}</span>}
          </div>
          {c.address && (
            <div className="acct-ref-customer-address">&#128205; {c.address}</div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Referrals > Orders sub-tab ────────────────────────────
const ReferralOrdersSubTab = ({ user }) => {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [page, setPage]       = useState(0);

  useEffect(() => {
    axios
      .get(`${API_URL}/menuRoutes/referral-orders`, {
        headers: { Authorization: `Bearer ${user.token}` },
      })
      .then(res => { setOrders(res.data); setLoading(false); })
      .catch(() => { setError('Could not load referral orders.'); setLoading(false); });
  }, [user.token]);

  if (loading) return <p className="acct-loading">Loading referral orders…</p>;
  if (error)   return <p className="acct-error">{error}</p>;
  if (orders.length === 0) return (
    <div className="acct-empty"><p>No orders from referred customers yet.</p></div>
  );

  const totalPages = Math.ceil(orders.length / PAGE_SIZE);
  const pageOrders = orders.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="acct-orders">
      {pageOrders.map((order) => (
        <div key={order.id} className="acct-order-card">
          <button
            className="acct-order-header"
            onClick={() => setExpanded(expanded === order.id ? null : order.id)}
          >
            <div className="acct-order-meta">
              <span className="acct-order-no">{order.order_no}</span>
              <span className="acct-order-customer">{order.customer_name}</span>
              <span className="acct-order-date">{formatDate(order.order_date)}</span>
            </div>
            <div className="acct-order-right">
              <span className={`acct-order-status ${statusClass(order)}`}>
                {statusLabel(order)}
              </span>
              <span className="acct-order-amount">{formatPrice(order.order_amount)}</span>
              <span className="acct-order-chevron">{expanded === order.id ? '▲' : '▼'}</span>
            </div>
          </button>

          {expanded === order.id && (
            <div className="acct-order-body">
              {(order.delivery_date || order.delivery_window) && (
                <div className="acct-delivery-info">
                  {order.delivery_date && <span>&#128337; {formatDate(order.delivery_date)}</span>}
                  {order.delivery_window && <span>&#128336; {order.delivery_window}</span>}
                </div>
              )}
              {order.lines.length > 0 ? (
                <table className="acct-lines-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th className="acct-col-num">Qty</th>
                      <th className="acct-col-num">Unit Price</th>
                      <th className="acct-col-num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.lines.map((line, i) => (
                      <tr key={i}>
                        <td>{line.product_name}</td>
                        <td className="acct-col-num">{line.qty}</td>
                        <td className="acct-col-num">{formatPrice(line.unit_price)}</td>
                        <td className="acct-col-num">{formatPrice(line.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} className="acct-total-label">Order Total</td>
                      <td className="acct-col-num acct-total-value">{formatPrice(order.order_amount)}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p className="acct-no-lines">No items on this order.</p>
              )}
            </div>
          )}
        </div>
      ))}
      {totalPages > 1 && (
        <div className="acct-pagination">
          <button className="acct-page-btn"
            onClick={() => { setPage(p => p - 1); setExpanded(null); }}
            disabled={page === 0}>&#8592;</button>
          <span className="acct-page-info">{page + 1} / {totalPages}</span>
          <button className="acct-page-btn"
            onClick={() => { setPage(p => p + 1); setExpanded(null); }}
            disabled={page === totalPages - 1}>&#8594;</button>
        </div>
      )}
    </div>
  );
};

// ── Stocks tab (store partner only) ──────────────────────
const StocksTab = ({ user }) => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = { Authorization: `Bearer ${user?.token}` };

  useEffect(() => {
    axios.get(`${API_URL}/stock/my-balance`, { headers: auth })
      .then(r => { setStocks(r.data); setLoading(false); })
      .catch(() => { setError('Failed to load stock'); setLoading(false); });
  }, []); // eslint-disable-line

  if (loading) return <div className="acct-loading">Loading…</div>;
  if (error)   return <div className="acct-error">{error}</div>;
  if (!stocks.length) return <div className="acct-no-lines">No stock on hand.</div>;

  return (
    <div className="stock-card-grid">
      {stocks.map(s => (
        <div key={s.product_id} className="stock-card">
          <p className="stock-card-name">{s.product_name}</p>
          <p className="stock-card-qty">{Math.floor(parseFloat(s.qty_on_hand))}</p>
          <p className="stock-card-label">Qty on Hand</p>
        </div>
      ))}
    </div>
  );
};

// ── Referrals tab (wrapper with Customers / Orders sub-tabs)
const ReferralsTab = ({ user }) => {
  const [subTab, setSubTab] = useState('customers');
  return (
    <div>
      <div className="acct-subtabs">
        <button
          className={`acct-subtab${subTab === 'customers' ? ' acct-subtab--active' : ''}`}
          onClick={() => setSubTab('customers')}
        >
          Customers
        </button>
        <button
          className={`acct-subtab${subTab === 'orders' ? ' acct-subtab--active' : ''}`}
          onClick={() => setSubTab('orders')}
        >
          Orders
        </button>
      </div>
      {subTab === 'customers' && <ReferredCustomersTab user={user} />}
      {subTab === 'orders'    && <ReferralOrdersSubTab user={user} />}
    </div>
  );
};

// ── Wallet tab ────────────────────────────────────────────
const TXN_LABEL = { LOAD: 'Added', SPEND: 'Spent', REWARD: 'Reward', REFERRAL: 'Referral' };
const TXN_CLASS = { LOAD: 'wlt-badge--load', SPEND: 'wlt-badge--spend', REWARD: 'wlt-badge--reward', REFERRAL: 'wlt-badge--referral' };
const IS_CREDIT  = { LOAD: true, SPEND: false, REWARD: true, REFERRAL: true };

const WalletTab = ({ user }) => {
  const authHeader = { Authorization: `Bearer ${user.token}` };
  const [balance, setBalance]         = useState(null);
  const [balLoading, setBalLoading]   = useState(true);
  const [txns, setTxns]               = useState([]);
  const [txnLoading, setTxnLoading]   = useState(false);
  const [txnPage, setTxnPage]         = useState(1);
  const [txnPages, setTxnPages]       = useState(0);
  const [phase, setPhase]             = useState('idle'); // idle | entering | processing
  const [loadAmount, setLoadAmount]   = useState('');
  const [error, setError]             = useState('');

  const fetchBalance = async () => {
    setBalLoading(true);
    try {
      const res = await axios.get(`${API_URL}/wallet/balance`, { headers: authHeader });
      setBalance(res.data);
    } catch {
      setError('Could not load wallet.');
    } finally {
      setBalLoading(false);
    }
  };

  const fetchTxns = async (page = 1) => {
    setTxnLoading(true);
    try {
      const res = await axios.get(`${API_URL}/wallet/transactions`,
        { params: { page }, headers: authHeader });
      setTxns(res.data.transactions);
      setTxnPages(res.data.pages);
      setTxnPage(page);
    } catch { /* silent */ } finally {
      setTxnLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
    fetchTxns(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddMoney = async () => {
    const amt = parseFloat(loadAmount);
    if (!amt || amt < 1) { setError('Enter a valid amount (min ₹1).'); return; }
    setError('');
    setPhase('processing');
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        setError('Failed to load payment SDK. Please check your connection and try again.');
        setPhase('idle');
        return;
      }

      const res = await axios.post(`${API_URL}/wallet/load/create-order`,
        { amount: amt }, { headers: authHeader });
      const { orderId, amount: orderAmt, currency, keyId } = res.data;

      const rzp = new window.Razorpay({
        key: keyId,
        amount: orderAmt,
        currency,
        order_id: orderId,
        name: 'Local Heros',
        description: 'Wallet Top-up',
        handler: async (payment) => {
          try {
            const vRes = await axios.post(`${API_URL}/wallet/load/verify`, {
              razorpay_order_id: payment.razorpay_order_id,
              razorpay_payment_id: payment.razorpay_payment_id,
              razorpay_signature: payment.razorpay_signature,
              amount: orderAmt,
            }, { headers: authHeader });
            setBalance((b) => ({ ...b, cash_balance: vRes.data.cash_balance }));
            setLoadAmount('');
            setPhase('idle');
            fetchTxns(1);
          } catch {
            setError('Payment received. Your wallet balance will be updated shortly.');
            setPhase('idle');
          }
        },
        modal: { ondismiss: () => setPhase('idle') },
        prefill: { name: user.name, email: user.email, contact: user.phone },
        theme: { color: '#D84315' },
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate payment.');
      setPhase('idle');
    }
  };

  return (
    <div className="wlt-tab">
      {/* Balance cards */}
      {balLoading ? (
        <p className="acct-loading">Loading wallet…</p>
      ) : balance ? (
        <div className="wlt-balance-row">
          <div className="wlt-balance-card">
            <span className="wlt-balance-label">Cash Balance</span>
            <span className="wlt-balance-value">₹{parseFloat(balance.cash_balance).toFixed(2)}</span>
          </div>
          <div className="wlt-balance-card wlt-balance-card--points">
            <span className="wlt-balance-label">Reward Points</span>
            <span className="wlt-balance-value">{parseFloat(balance.points_balance).toFixed(0)} pts</span>
          </div>
        </div>
      ) : null}

      {/* Add money */}
      <div className="wlt-load-section">
        {phase === 'idle' && (
          <button className="wlt-add-btn" onClick={() => setPhase('entering')}>
            + Add Money
          </button>
        )}
        {phase === 'entering' && (
          <div className="wlt-load-form">
            <input
              className="wlt-amount-input"
              type="number"
              min="1"
              max="100000"
              placeholder="Amount in ₹"
              value={loadAmount}
              onChange={(e) => setLoadAmount(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddMoney()}
              autoFocus
            />
            <button className="wlt-pay-btn" onClick={handleAddMoney}>Pay</button>
            <button className="wlt-cancel-btn"
              onClick={() => { setPhase('idle'); setLoadAmount(''); setError(''); }}>
              Cancel
            </button>
          </div>
        )}
        {phase === 'processing' && <p className="acct-loading">Opening payment…</p>}
      </div>

      {error && <p className="acct-error" style={{ margin: '0 0 12px' }}>{error}</p>}

      {/* Transaction history */}
      <div className="wlt-txn-section">
        <p className="wlt-txn-heading">Recent Transactions</p>
        {txnLoading ? (
          <p className="acct-loading">Loading…</p>
        ) : txns.length === 0 ? (
          <div className="acct-empty"><p>No transactions yet.</p></div>
        ) : (
          <>
            <ul className="wlt-txn-list">
              {txns.map((t) => (
                <li key={t.id} className="wlt-txn-row">
                  <span className={`wlt-badge ${TXN_CLASS[t.txn_type]}`}>
                    {TXN_LABEL[t.txn_type]}
                  </span>
                  <div className="wlt-txn-desc">
                    <span className="wlt-txn-description">{t.description || t.txn_type}</span>
                    <span className="wlt-txn-date">{formatDate(t.created_date)}</span>
                  </div>
                  <div className="wlt-txn-amounts">
                    <span className={`wlt-txn-amount ${IS_CREDIT[t.txn_type] ? 'wlt-credit' : 'wlt-debit'}`}>
                      {IS_CREDIT[t.txn_type] ? '+' : '−'}₹{parseFloat(t.amount).toFixed(2)}
                    </span>
                    <span className="wlt-txn-balance">
                      Bal: ₹{parseFloat(t.balance_after).toFixed(2)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            {txnPages > 1 && (
              <div className="acct-pagination">
                <button className="acct-page-btn"
                  onClick={() => fetchTxns(txnPage - 1)} disabled={txnPage === 1}>
                  &#8592;
                </button>
                <span className="acct-page-info">{txnPage} / {txnPages}</span>
                <button className="acct-page-btn"
                  onClick={() => fetchTxns(txnPage + 1)} disabled={txnPage === txnPages}>
                  &#8594;
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── helpers ───────────────────────────────────────────────
const encodeRef = (payload) =>
  btoa(unescape(encodeURIComponent(JSON.stringify(payload))))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

const BASE_URL = window.location.origin;

// ── Refer a Customer tab ──────────────────────────────────
const cleanPhone = (raw) => {
  let p = (raw || '').replace(/[\s\-.]/g, '');
  if (p.startsWith('+91')) p = p.slice(3);
  else if (/^91\d{10}$/.test(p)) p = p.slice(2);
  return p;
};

const ReferTab = ({ user }) => {
  const [form, setForm]       = useState({ name: '', phone: '', email: '', address: '' });
  const [link, setLink]       = useState('');
  const [waUrl, setWaUrl]     = useState('');
  const [copied, setCopied]   = useState(false);
  const [error, setError]     = useState('');
  const [checking, setChecking] = useState(false);

  const buildLink = async () => {
    const phone = cleanPhone(form.phone);
    if (!form.name.trim()) { setError('Customer name is required.'); return null; }
    if (!phone)             { setError('Phone number is required.'); return null; }
    if (!/^\d{10}$/.test(phone)) { setError('Enter a valid 10-digit phone number.'); return null; }
    const email = form.email.trim().toLowerCase();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address.'); return null;
    }

    const phoneRes = await axios.get(`${API_URL}/authRoutes/check-phone`, { params: { phone } });
    if (phoneRes.data.exists) { setError('This phone number is already registered.'); return null; }

    if (email) {
      const emailRes = await axios.get(`${API_URL}/authRoutes/check-email`, { params: { email } });
      if (emailRes.data.exists) { setError('This email address is already registered.'); return null; }
    }

    const pincodes = [...new Set((form.address.match(/\b\d{6}\b/g) || []))];
    if (pincodes.length > 0) {
      let deliverable = false;
      for (const pincode of pincodes) {
        const pinRes = await axios.get(`${API_URL}/authRoutes/check-pincode`, { params: { pincode } });
        if (pinRes.data.deliverable) { deliverable = true; break; }
      }
      if (!deliverable) { setError('Currently we are not delivering at this pincode.'); return null; }
    }

    const payload = {
      n:   form.name.trim(),
      p:   phone,
      rid: user.id,
      rn:  user.name || user.username || '',
    };
    if (email)               payload.e = email;
    if (form.address.trim()) payload.a = form.address.trim();
    return `${BASE_URL}/register?ref=${encodeRef(payload)}`;
  };

  const handleGenerate = async () => {
    setError(''); setLink('');
    setChecking(true);
    try {
      const generated = await buildLink();
      if (generated) setLink(generated);
    } catch {
      setError('Could not validate details. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleInvite = async () => {
    setError(''); setLink(''); setWaUrl('');
    setChecking(true);
    try {
      const generated = await buildLink();
      if (!generated) return;
      setLink(generated);
      const phone = cleanPhone(form.phone);
      const senderName = user.name || user.username || '';
      const message =
        `Hi ${form.name.trim()}! 👋\n\n` +
        `I'd like to invite you to The New Luxury – clean, curated food delivered to your door.\n\n` +
        `Use this personal link to register:\n${generated}\n\n` +
        `Regards,\n${senderName}\nThe New Luxury`;
      setWaUrl(`https://wa.me/91${phone}?text=${encodeURIComponent(message)}`);
    } catch {
      setError('Could not validate details. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleCopy = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => fallbackCopy());
    } else {
      fallbackCopy();
    }
  };

  const fallbackCopy = () => {
    const ta = document.createElement('textarea');
    ta.value = link;
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setError('Could not copy — please copy the link manually.');
    }
    document.body.removeChild(ta);
  };

  return (
    <div className="acct-refer">
      <p className="acct-refer-desc">
        Fill in the customer details to generate a shareable registration link.
      </p>

      {error && <p className="acct-refer-error">{error}</p>}

      <div className="acct-refer-form">
        <div className="acct-refer-field">
          <label>Customer Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={e => { setForm({ ...form, name: e.target.value }); setWaUrl(''); }}
            placeholder="Enter customer name"
          />
        </div>
        <div className="acct-refer-field">
          <label>Phone Number *</label>
          <input
            type="tel"
            value={form.phone}
            onChange={e => { setForm({ ...form, phone: e.target.value }); setWaUrl(''); }}
            placeholder="Enter phone number"
          />
        </div>
        <div className="acct-refer-field">
          <label>Email Address <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional)</span></label>
          <input
            type="email"
            value={form.email}
            onChange={e => { setForm({ ...form, email: e.target.value }); setWaUrl(''); }}
            placeholder="Enter email address"
          />
        </div>
        <div className="acct-refer-field">
          <label>Address <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional)</span></label>
          <textarea
            value={form.address}
            onChange={e => { setForm({ ...form, address: e.target.value }); setWaUrl(''); }}
            placeholder="Enter delivery address"
            rows={4}
            style={{ width: '100%', resize: 'vertical', fontSize: '0.95rem', padding: '8px 10px', boxSizing: 'border-box' }}
          />
        </div>
      </div>

      <div className="acct-refer-actions">
        <button className="acct-refer-btn acct-refer-btn--primary" onClick={handleGenerate} disabled={checking}>
          {checking ? 'Checking…' : 'Generate Referral Link'}
        </button>
        {waUrl ? (
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="acct-refer-btn acct-refer-btn--invite"
          >
            &#128172; Open WhatsApp
          </a>
        ) : (
          <button className="acct-refer-btn acct-refer-btn--invite" onClick={handleInvite} disabled={checking}>
            {checking ? 'Preparing…' : <span>&#128172; Invite via WhatsApp</span>}
          </button>
        )}
      </div>

      {link && (
        <div className="acct-refer-link-row">
          <span className="acct-refer-link">{link}</span>
          <button className="acct-refer-copy" onClick={handleCopy} title="Copy link">
            {copied ? '✓' : '📋'}
          </button>
        </div>
      )}
    </div>
  );
};

// ── Subscriptions tab ─────────────────────────────────────
const DAYS_LABEL = { AD: 'Every Day', WD: 'Weekdays', WE: 'Weekends' };
const SUB_STATUS_CLASS = {
  active:    'sub-status--active',
  paused:    'sub-status--paused',
  cancelled: 'sub-status--cancelled',
};

const SubscriptionsTab = ({ user }) => {
  const authHeader = { Authorization: `Bearer ${user.token}` };
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/subscriptions`, { headers: authHeader })
      .then(res => { setSubscriptions(res.data); setLoading(false); })
      .catch(() => { setError('Could not load subscriptions.'); setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePause = async (id) => {
    setActionError(null);
    setActionLoading(id);
    try {
      await axios.patch(`${API_URL}/subscriptions/${id}/pause`, {}, { headers: authHeader });
      setSubscriptions(prev => prev.map(s => s.id === id ? { ...s, status: 'paused' } : s));
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not pause subscription.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async (id) => {
    setActionError(null);
    setActionLoading(id);
    try {
      await axios.patch(`${API_URL}/subscriptions/${id}/resume`, {}, { headers: authHeader });
      setSubscriptions(prev => prev.map(s => s.id === id ? { ...s, status: 'active' } : s));
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not resume subscription.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (id) => {
    setActionError(null);
    if (!window.confirm('Cancel this subscription? Future scheduled orders will be skipped.')) return;
    setActionLoading(id);
    try {
      await axios.delete(`${API_URL}/subscriptions/${id}`, { headers: authHeader });
      setSubscriptions(prev => prev.map(s => s.id === id ? { ...s, status: 'cancelled' } : s));
    } catch (err) {
      setActionError(err.response?.data?.error || 'Could not cancel subscription.');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <p className="acct-loading">Loading subscriptions…</p>;
  if (error)   return <p className="acct-error">{error}</p>;

  if (subscriptions.length === 0) return (
    <div className="acct-empty">
      <p>No active subscriptions.</p>
      <p style={{ fontSize: '13px', marginTop: '8px' }}>
        Subscribe to a product from the Home page to set up recurring deliveries.
      </p>
    </div>
  );

  return (
    <div className="acct-subs">
      {actionError && <p className="acct-error" style={{ marginBottom: '12px' }}>{actionError}</p>}

      {subscriptions.map(sub => (
        <div key={sub.id} className="acct-order-card">
          <button
            className="acct-order-header"
            onClick={() => setExpanded(expanded === sub.id ? null : sub.id)}
          >
            <div className="acct-order-meta">
              <span className="acct-order-no">{sub.product_name}</span>
              <span className="acct-order-date">
                {DAYS_LABEL[sub.days_of_week] || sub.days_of_week}
                {' · '}Qty {sub.quantity}
                {' · '}&#8377;{parseFloat(sub.unit_price).toFixed(2)}/delivery
              </span>
            </div>
            <div className="acct-order-right">
              <span className={`acct-order-status ${SUB_STATUS_CLASS[sub.status] || 'status-default'}`}>
                {sub.status}
              </span>
              <span className="acct-order-chevron">{expanded === sub.id ? '▲' : '▼'}</span>
            </div>
          </button>

          {expanded === sub.id && (
            <div className="acct-order-body">
              <div className="acct-sub-details">
                {sub.delivery_window && (
                  <div className="acct-sub-detail-row">
                    <span className="acct-sub-detail-label">Window</span>
                    <span>{sub.delivery_window}</span>
                  </div>
                )}
                <div className="acct-sub-detail-row">
                  <span className="acct-sub-detail-label">From</span>
                  <span>{formatDate(sub.start_date)}</span>
                </div>
                {sub.end_date && (
                  <div className="acct-sub-detail-row">
                    <span className="acct-sub-detail-label">Until</span>
                    <span>{formatDate(sub.end_date)}</span>
                  </div>
                )}
                {!sub.end_date && (
                  <div className="acct-sub-detail-row">
                    <span className="acct-sub-detail-label">Duration</span>
                    <span>Ongoing</span>
                  </div>
                )}
              </div>

              {sub.status !== 'cancelled' && (
                <div className="acct-sub-actions">
                  {sub.status === 'active' && (
                    <button
                      className="acct-sub-btn acct-sub-btn--pause"
                      onClick={() => handlePause(sub.id)}
                      disabled={actionLoading === sub.id}
                    >
                      {actionLoading === sub.id ? '…' : 'Pause'}
                    </button>
                  )}
                  {sub.status === 'paused' && (
                    <button
                      className="acct-sub-btn acct-sub-btn--resume"
                      onClick={() => handleResume(sub.id)}
                      disabled={actionLoading === sub.id}
                    >
                      {actionLoading === sub.id ? '…' : 'Resume'}
                    </button>
                  )}
                  <button
                    className="acct-sub-btn acct-sub-btn--cancel"
                    onClick={() => handleCancel(sub.id)}
                    disabled={actionLoading === sub.id}
                  >
                    {actionLoading === sub.id ? '…' : 'Cancel'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Weekly Payments tab ───────────────────────────────────
const WeeklyPaymentsTab = ({ user }) => {
  const authHeader = { Authorization: `Bearer ${user.token}` };
  const [weeks, setWeeks]       = useState([]);
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [payError, setPayError] = useState('');
  const [payingKey, setPayingKey] = useState(null);

  const [expandedWeek, setExpandedWeek]               = useState(null);
  const [expandedHistoryWeek, setExpandedHistoryWeek] = useState(null);
  const [expandedOrder, setExpandedOrder]             = useState(null);
  const [orderDetails, setOrderDetails]               = useState({});
  const [orderDetailsLoading, setOrderDetailsLoading] = useState({});

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/pay/weekly-summary`, { headers: authHeader }),
        axios.get(`${API_URL}/pay/weekly-payment-history`, { headers: authHeader }),
      ]);
      setWeeks(summaryRes.data);
      setHistory(historyRes.data);
    } catch {
      setError('Could not load payments.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const fmtWeekLabel = (start, end) => `${formatDate(start)} – ${formatDate(end)}`;

  const toggleWeek = (weekStart) => {
    setExpandedWeek(prev => prev === weekStart ? null : weekStart);
    setExpandedOrder(null);
  };

  const toggleOrder = async (orderId) => {
    if (expandedOrder === orderId) { setExpandedOrder(null); return; }
    setExpandedOrder(orderId);
    if (!orderDetails[orderId]) {
      setOrderDetailsLoading(prev => ({ ...prev, [orderId]: true }));
      try {
        const { data } = await axios.get(
          `${API_URL}/sales-orders/${orderId}/commission`,
          { headers: authHeader }
        );
        setOrderDetails(prev => ({ ...prev, [orderId]: data }));
      } catch {
        setOrderDetails(prev => ({ ...prev, [orderId]: null }));
      } finally {
        setOrderDetailsLoading(prev => ({ ...prev, [orderId]: false }));
      }
    }
  };

  const handlePayNow = async (week) => {
    setPayError('');
    setPayingKey(week.week_start);

    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setPayError('Failed to load payment SDK. Please check your connection.');
      setPayingKey(null);
      return;
    }

    try {
      const { data } = await axios.post(
        `${API_URL}/pay/weekly-payment/create-order`,
        { week_start: week.week_start, order_ids: (week.orders || []).map(o => o.id) },
        { headers: authHeader }
      );

      const cancelPending = () =>
        axios.post(`${API_URL}/pay/weekly-payment/cancel`,
          { razorpay_order_id: data.razorpay_order_id },
          { headers: authHeader }
        ).catch(() => {});

      const rzp = new window.Razorpay({
        key:      data.key,
        amount:   data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name:     'Local Heros',
        description: `Weekly payment (${fmtWeekLabel(week.week_start, week.week_end)})`,
        prefill: { name: user.name, email: user.email, contact: user.phone },
        theme:   { color: '#C9A227' },
        handler: async (response) => {
          try {
            await axios.post(`${API_URL}/pay/weekly-payment/verify`, {
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            }, { headers: authHeader });
            setPayingKey(null);
            fetchData();
          } catch {
            setPayError('Payment received but update failed. Please contact support.');
            setPayingKey(null);
          }
        },
        modal: {
          ondismiss: () => {
            cancelPending();
            setPayingKey(null);
          },
        },
      });
      rzp.on('payment.failed', (response) => {
        cancelPending();
        setPayError(`Payment failed: ${response.error.description}`);
        setPayingKey(null);
      });
      rzp.open();
    } catch (err) {
      setPayError(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
      setPayingKey(null);
    }
  };

  if (loading) return <p className="acct-loading">Loading payments…</p>;
  if (error)   return <p className="acct-error">{error}</p>;
  if (weeks.length === 0 && history.length === 0) return (
    <div className="acct-empty"><p>No payments yet.</p></div>
  );

  return (
    <div className="wkpay-list">
      {payError && <p className="acct-error" style={{ marginBottom: 12 }}>{payError}</p>}

      {/* Pending weeks */}
      {weeks.map(week => {
        const isOpen = expandedWeek === week.week_start;
        return (
          <div key={week.week_start} className="wkpay-card">
            {/* Card header row — click info side to expand */}
            <div className="wkpay-card-header">
              <div className="wkpay-info" onClick={() => toggleWeek(week.week_start)} style={{ cursor: 'pointer' }}>
                <span className="wkpay-label">
                  {fmtWeekLabel(week.week_start, week.week_end)}
                  <span className="wkpay-chevron">{isOpen ? ' ▲' : ' ▼'}</span>
                </span>
                <span className="wkpay-meta">
                  {week.order_count} order{week.order_count !== 1 ? 's' : ''}
                  {!week.is_payable && <span className="wkpay-pending-note"> · payable on Sunday</span>}
                </span>
                {week.total_commission > 0 && (
                  <span className="wkpay-commission-row">
                    <span className="wkpay-gross">{formatPrice(week.gross_total)}</span>
                    <span className="wkpay-commission-deduct">− {formatPrice(week.total_commission)} commission</span>
                  </span>
                )}
              </div>
              <div className="wkpay-right">
                <span className="wkpay-total">{formatPrice(week.net_total)}</span>
                <button
                  className="wkpay-pay-btn"
                  onClick={() => handlePayNow(week)}
                  disabled={!week.is_payable || payingKey === week.week_start}
                >
                  {payingKey === week.week_start ? 'Opening…' : 'Pay Now'}
                </button>
              </div>
            </div>

            {/* Expanded order list */}
            {isOpen && (
              <div className="wkpay-orders">
                {(week.orders || []).map(order => {
                  const orderOpen = expandedOrder === order.id;
                  const detail    = orderDetails[order.id];
                  const detLoading = orderDetailsLoading[order.id];
                  return (
                    <div key={order.id} className="wkpay-order-row">
                      <div className="wkpay-order-header" onClick={() => toggleOrder(order.id)}>
                        <div className="wkpay-order-left">
                          <span className="wkpay-order-no">{order.order_no}</span>
                          <span className="wkpay-order-date">{formatDate(order.delivery_date || order.order_date)}</span>
                        </div>
                        <div className="wkpay-order-right">
                          <span className="wkpay-order-amount">{formatPrice(order.order_amount)}</span>
                          {order.commission > 0 && (
                            <span className="wkpay-order-comm">−{formatPrice(order.commission)}</span>
                          )}
                          <span className="wkpay-chevron-sm">{orderOpen ? '▲' : '▼'}</span>
                        </div>
                      </div>

                      {/* Product line details */}
                      {orderOpen && (
                        <div className="wkpay-lines">
                          {detLoading && <p className="wkpay-lines-msg">Loading…</p>}
                          {!detLoading && !detail && <p className="wkpay-lines-msg wkpay-lines-err">Failed to load details.</p>}
                          {!detLoading && detail && (
                            <table className="wkpay-lines-table">
                              <thead>
                                <tr>
                                  <th>Product</th>
                                  <th className="wkpay-num">Qty</th>
                                  <th className="wkpay-num">Price</th>
                                  <th className="wkpay-num">Pre-GST</th>
                                  <th className="wkpay-num">Comm%</th>
                                  <th className="wkpay-num">Commission</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detail.lines.map(line => (
                                  <tr key={line.line_id}>
                                    <td>{line.product_name}</td>
                                    <td className="wkpay-num">{line.qty}</td>
                                    <td className="wkpay-num">{formatPrice(line.unit_price)}</td>
                                    <td className="wkpay-num">{formatPrice(line.pre_gst_unit_price)}</td>
                                    <td className="wkpay-num">{line.commission_percent}%</td>
                                    <td className="wkpay-num">{formatPrice(line.line_commission)}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td colSpan={5} className="wkpay-lines-total-label">Order total commission</td>
                                  <td className="wkpay-num wkpay-lines-total">{formatPrice(detail.total_commission)}</td>
                                </tr>
                              </tfoot>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Payment history */}
      {history.length > 0 && (
        <>
          <p className="wkpay-history-heading">Payment History</p>
          {history.map(entry => {
            const isOpen = expandedHistoryWeek === entry.week_start;
            return (
              <div key={entry.week_start} className="wkpay-card wkpay-card--paid">
                <div className="wkpay-card-header">
                  <div className="wkpay-info" onClick={() => setExpandedHistoryWeek(prev => prev === entry.week_start ? null : entry.week_start)} style={{ cursor: 'pointer' }}>
                    <span className="wkpay-label">
                      {fmtWeekLabel(entry.week_start, entry.week_end)}
                      <span className="wkpay-chevron">{isOpen ? ' ▲' : ' ▼'}</span>
                    </span>
                    <span className="wkpay-meta">
                      {entry.order_count} order{entry.order_count !== 1 ? 's' : ''}
                      {' · '}Paid on {formatDate(entry.completed_date)}
                    </span>
                    {entry.total_commission > 0 && (
                      <span className="wkpay-commission-row">
                        <span className="wkpay-gross">{formatPrice(entry.gross_total)}</span>
                        <span className="wkpay-commission-deduct">− {formatPrice(entry.total_commission)} commission</span>
                      </span>
                    )}
                  </div>
                  <div className="wkpay-right">
                    <span className="wkpay-total">{formatPrice(entry.net_total)}</span>
                    <span className="wkpay-paid-badge">Paid</span>
                  </div>
                </div>

                {isOpen && (
                  <div className="wkpay-orders">
                    {(entry.orders || []).map(order => {
                      const orderOpen  = expandedOrder === order.id;
                      const detail     = orderDetails[order.id];
                      const detLoading = orderDetailsLoading[order.id];
                      return (
                        <div key={order.id} className="wkpay-order-row">
                          <div className="wkpay-order-header" onClick={() => toggleOrder(order.id)}>
                            <div className="wkpay-order-left">
                              <span className="wkpay-order-no">{order.order_no}</span>
                              <span className="wkpay-order-date">{formatDate(order.delivery_date || order.order_date)}</span>
                            </div>
                            <div className="wkpay-order-right">
                              <span className="wkpay-order-amount">{formatPrice(order.order_amount)}</span>
                              {order.commission > 0 && (
                                <span className="wkpay-order-comm">−{formatPrice(order.commission)}</span>
                              )}
                              <span className="wkpay-chevron-sm">{orderOpen ? '▲' : '▼'}</span>
                            </div>
                          </div>

                          {orderOpen && (
                            <div className="wkpay-lines">
                              {detLoading && <p className="wkpay-lines-msg">Loading…</p>}
                              {!detLoading && !detail && <p className="wkpay-lines-msg wkpay-lines-err">Failed to load details.</p>}
                              {!detLoading && detail && (
                                <table className="wkpay-lines-table">
                                  <thead>
                                    <tr>
                                      <th>Product</th>
                                      <th className="wkpay-num">Qty</th>
                                      <th className="wkpay-num">Price</th>
                                      <th className="wkpay-num">Pre-GST</th>
                                      <th className="wkpay-num">Comm%</th>
                                      <th className="wkpay-num">Commission</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detail.lines.map(line => (
                                      <tr key={line.line_id}>
                                        <td>{line.product_name}</td>
                                        <td className="wkpay-num">{line.qty}</td>
                                        <td className="wkpay-num">{formatPrice(line.unit_price)}</td>
                                        <td className="wkpay-num">{formatPrice(line.pre_gst_unit_price)}</td>
                                        <td className="wkpay-num">{line.commission_percent}%</td>
                                        <td className="wkpay-num">{formatPrice(line.line_commission)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr>
                                      <td colSpan={5} className="wkpay-lines-total-label">Order total commission</td>
                                      <td className="wkpay-num wkpay-lines-total">{formatPrice(detail.total_commission)}</td>
                                    </tr>
                                  </tfoot>
                                </table>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

// ── Account page ──────────────────────────────────────────
const Account = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const isPartner = user?.user_type === 'P' || user?.user_type === 'S';
  const isStorePartner = user?.user_type === 'S';
  const [tab, setTab] = useState(user?.user_type === 'S' ? 'stocks' : 'orders');
  const [checkoutMode, setCheckoutModeState] = useState(
    () => localStorage.getItem('checkoutMode') || 'live'
  );

  const setCheckoutMode = (mode) => {
    localStorage.setItem('checkoutMode', mode);
    setCheckoutModeState(mode);
  };

  return (
    <div className="acct-container">
      <div className="acct-header">
        <div className="acct-avatar">
          {(user?.name || user?.username || 'U')[0].toUpperCase()}
        </div>
        <div>
          <h2 className="acct-name">{user?.name || user?.username}</h2>
          <p className="acct-email">{user?.email || user?.username}</p>
        </div>
        {isStorePartner && (
          <div className="acct-mode-toggle">
            <button
              className={`acct-mode-btn${checkoutMode === 'live' ? ' acct-mode-btn--active' : ''}`}
              onClick={() => setCheckoutMode('live')}
            >
              Live
            </button>
            <button
              className={`acct-mode-btn${checkoutMode === 'prebook' ? ' acct-mode-btn--active' : ''}`}
              onClick={() => setCheckoutMode('prebook')}
            >
              Prebook
            </button>
          </div>
        )}
      </div>

      <div className="acct-tabs">
        {isStorePartner && (
          <button
            className={`acct-tab ${tab === 'stocks' ? 'acct-tab--active' : ''}`}
            onClick={() => setTab('stocks')}
          >
            &#128230; Stocks
          </button>
        )}
        <button
          className={`acct-tab ${tab === 'orders' ? 'acct-tab--active' : ''}`}
          onClick={() => setTab('orders')}
        >
          &#128230; Orders
        </button>
        {isPartner && (
          <button
            className={`acct-tab ${tab === 'refer' ? 'acct-tab--active' : ''}`}
            onClick={() => setTab('refer')}
          >
            &#128101; New Customer
          </button>
        )}
        {isPartner && (
          <button
            className={`acct-tab ${tab === 'referrals' ? 'acct-tab--active' : ''}`}
            onClick={() => setTab('referrals')}
          >
            &#128101; Customers
          </button>
        )}
        {user?.bypass_payment === 'Y' && (
          <button
            className={`acct-tab ${tab === 'payments' ? 'acct-tab--active' : ''}`}
            onClick={() => setTab('payments')}
          >
            &#128176; Payments
          </button>
        )}
        {!isStorePartner && (
          <button
            className={`acct-tab ${tab === 'wallet' ? 'acct-tab--active' : ''}`}
            onClick={() => setTab('wallet')}
          >
            &#128179; Wallet
          </button>
        )}
        <button
          className={`acct-tab ${tab === 'details' ? 'acct-tab--active' : ''}`}
          onClick={() => setTab('details')}
        >
          &#128100; Personal Details
        </button>
      </div>

      <div className="acct-panel">
        {tab === 'stocks'    && isStorePartner && <StocksTab user={user} />}
        {tab === 'orders'    && <OrdersTab user={user} />}
        {tab === 'payments' && user?.bypass_payment === 'Y' && <WeeklyPaymentsTab user={user} />}
        {tab === 'refer'     && isPartner && <ReferTab user={user} />}
        {tab === 'referrals' && isPartner && <ReferralsTab user={user} />}
        {tab === 'wallet'          && <WalletTab user={user} />}
        {tab === 'details'         && <PersonalDetailsTab user={user} />}
      </div>
    </div>
  );
};

export default Account;
